/** Taskforce building blocks, styled after ClickUp: icons, status chips, type icons, avatars, a people picker. */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { TypeGlyph } from './typeicons';
import { initials, useTfMeta, type TfMeta, type TfPerson, type TfStatus, type TfType } from './data';

/**
 * Closes a pop-up on a press anywhere outside it, or on Esc (22 Oct 2026). `ref` goes on what holds the pop-up and the
 * button that opened it. Only the pop-up and that button (the one with aria-expanded) count as inside: a press on the
 * blank space beside them, or on any other control, closes it, as in ClickUp.
 */
export function useAway<T extends HTMLElement = HTMLDivElement>(open: boolean, close: () => void) {
  const ref = useRef<T>(null);
  const latest = useRef(close);
  useEffect(() => {
    latest.current = close;
  });
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const hit = (e.target as Element | null)?.closest?.('.tfpop, .tfcustom, [aria-expanded="true"]');
      if (!ref.current || !hit || !ref.current.contains(hit)) latest.current();
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && latest.current();
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return ref;
}

// ------------------------------------------------------------------ icons (simple line icons, 16px)

const P: Record<string, ReactNode> = {
  list: <path d="M5 4h9M5 8h9M5 12h9M2 4h.01M2 8h.01M2 12h.01" />,
  board: <path d="M2.5 2.5h3v11h-3zM6.5 2.5h3v7h-3zM10.5 2.5h3v9h-3z" />,
  plus: <path d="M8 3v10M3 8h10" />,
  sliders: (
    <>
      <path d="M2 4h6.5M11.5 4H14M2 8h1.5M6.5 8H14M2 12h7.5M12.5 12H14" />
      <circle cx="10" cy="4" r="1.5" />
      <circle cx="5" cy="8" r="1.5" />
      <circle cx="11" cy="12" r="1.5" />
    </>
  ),
  dblRight: <path d="M3.5 4l4 4-4 4M8.5 4l4 4-4 4" />,
  dblLeft: <path d="M12.5 4l-4 4 4 4M7.5 4l-4 4 4 4" />,
  pulse: <path d="M1.5 8.5h3l1.8-4.5 3.4 8 1.8-3.5h3" />,
  clip: <path d="M10.5 4.5l-4.8 4.8a1.4 1.4 0 0 0 2 2l5-5a2.8 2.8 0 0 0-4-4l-5 5a4.2 4.2 0 0 0 6 6l4.3-4.3" />,
  search: <path d="M7 12A5 5 0 1 0 7 2a5 5 0 0 0 0 10zM14 14l-3.5-3.5" />,
  chevDown: <path d="M4 6l4 4 4-4" />,
  chevRight: <path d="M6 4l4 4-4 4" />,
  chevLeft: <path d="M10 4l-4 4 4 4" />,
  chevUp: <path d="M4 10l4-4 4 4" />,
  x: <path d="M4 4l8 8M12 4l-8 8" />,
  flag: <path d="M3.5 14V2.5M3.5 3h8l-1.6 2.8L11.5 9h-8" />,
  calendar: <path d="M2.5 4h11v9.5h-11zM2.5 7h11M5.5 2.5v3M10.5 2.5v3" />,
  upload: <path d="M8 10.5V2.5M5 5.5l3-3 3 3M2.5 10v3.5h11V10" />,
  table: <path d="M2.5 3h11v10h-11zM2.5 6.5h11M2.5 10h11M6 3v10" />,
  user: <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM2.5 14c.6-2.6 2.8-4 5.5-4s4.9 1.4 5.5 4" />,
  users: (
    <path d="M6 7.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM1.5 13.5c.5-2.3 2.3-3.5 4.5-3.5s4 1.2 4.5 3.5M11 2.7a2.5 2.5 0 0 1 0 4.6M12.5 10.2c1 .6 1.7 1.7 2 3.3" />
  ),
  status: <path d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12zM8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />,
  check: <path d="M3.5 8.5l3 3 6-7" />,
  home: <path d="M2.5 7.5L8 3l5.5 4.5V13.5h-11z" />,
  gear: (
    <path d="M8 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM8 1.8v1.7M8 12.5v1.7M1.8 8h1.7M12.5 8h1.7M3.6 3.6l1.2 1.2M11.2 11.2l1.2 1.2M3.6 12.4l1.2-1.2M11.2 4.8l1.2-1.2" />
  ),
  chart: <path d="M2.5 13.5h11M4.5 11V7M8 11V4M11.5 11V8.5" />,
  clock: <path d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12zM8 5v3.5l2.5 1.5" />,
  shield: <path d="M8 2l5 2v4c0 3-2.2 5.2-5 6-2.8-.8-5-3-5-6V4z" />,
  subtask: <path d="M4 2.5v6.5a2 2 0 0 0 2 2h6M10 9l2 2-2 2" />,
  comment: <path d="M2.5 3.5h11v7.5H7l-3 2.5V11H2.5z" />,
  smile: (
    <>
      <circle cx="8" cy="8" r="5.8" />
      <path d="M5.6 9.6a3 3 0 0 0 4.8 0M6 6.4h.01M10 6.4h.01" />
    </>
  ),
  send: <path d="M8 13V3M3.8 7.2L8 3l4.2 4.2" />,
  thumb: (
    <path d="M5 7.2V13H2.8V7.2zM5 7.2l2.4-4.4c.9 0 1.6.7 1.6 1.6v1.8h3.3c.8 0 1.4.7 1.2 1.5l-1 4.3c-.1.6-.7 1-1.3 1H5" />
  ),
  desc: <path d="M3 4h10M3 7h10M3 10h6" />,
  dots: <path d="M3.5 8h.01M8 8h.01M12.5 8h.01" />,
  trash: <path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 9h5.8l.6-9" />,
  arrowRight: <path d="M3 8h10M9.5 4.5L13 8l-3.5 3.5" />,
  filter: <path d="M2.5 3.5h11L9.5 8.5v4l-3 1.5V8.5z" />,
  logout: <path d="M6.5 13.5h-4v-11h4M10 5l3 3-3 3M13 8H6" />,
  layers: <path d="M8 2.5l5.5 3L8 8.5l-5.5-3zM2.5 8.5L8 11.5l5.5-3M2.5 11L8 14l5.5-3" />,
  bolt: <path d="M9 1.5L3.5 9H8l-1 5.5L12.5 7H8z" />,
  grid: <path d="M2.5 2.5h4v4h-4zM9.5 2.5h4v4h-4zM2.5 9.5h4v4h-4zM9.5 9.5h4v4h-4z" />,
  collapse: <path d="M7.5 4L3.5 8l4 4M12.5 4l-4 4 4 4" />,
  expand: <path d="M8.5 4l4 4-4 4M3.5 4l4 4-4 4" />,
  star: <path d="M8 2l1.8 3.8 4.2.5-3.1 2.9.8 4.1L8 11.3l-3.7 2 .8-4.1L2 6.3l4.2-.5z" />,
  copy: <path d="M5.5 5.5V3h7.5v7.5h-2.5M3 5.5h7.5V13H3z" />,
  link: <path d="M6.5 9.5l3-3M7 4.5l1-1a2.8 2.8 0 0 1 4 4l-1 1M9 11.5l-1 1a2.8 2.8 0 0 1-4-4l1-1" />,
  edit: <path d="M10.5 2.5l3 3-8 8h-3v-3zM9 4l3 3" />,
  share: <path d="M8 10V2.5M5 5.5l3-3 3 3M3 9v4.5h10V9" />,
  sun: (
    <path d="M8 10.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.4 3.4l1 1M11.6 11.6l1 1M3.4 12.6l1-1M11.6 4.4l1-1" />
  ),
  moon: <path d="M13.5 9.8A5.6 5.6 0 0 1 6.2 2.5a5.6 5.6 0 1 0 7.3 7.3z" />,
  bell: <path d="M4 11V7.5a4 4 0 0 1 8 0V11l1.2 1.5H2.8zM6.5 13.5a1.6 1.6 0 0 0 3 0" />,
  'bell-off': <path d="M4 11V7.5a4 4 0 0 1 8 0V11l1.2 1.5H2.8zM6.5 13.5a1.6 1.6 0 0 0 3 0M2 2l12 12" />,
  lock: <path d="M4 7.5h8v6H4zM5.8 7.5V5.3a2.2 2.2 0 0 1 4.4 0v2.2" />,
  mail: <path d="M2 3.5h12v9H2zM2.3 3.9L8 8.4l5.7-4.5" />,
  // The free trial (22 Oct 2026): view only, the tour, and feedback.
  eye: (
    <path d="M1.5 8s2.4-4.5 6.5-4.5S14.5 8 14.5 8 12.1 12.5 8 12.5 1.5 8 1.5 8zM8 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
  ),
  sparkle: (
    <path d="M8 1.5l1.4 4.1 4.1 1.4-4.1 1.4L8 12.5 6.6 8.4 2.5 7l4.1-1.4zM12.8 11.2l.5 1.4 1.4.5-1.4.5-.5 1.4-.5-1.4-1.4-.5 1.4-.5z" />
  ),
  megaphone: <path d="M2.5 6.5v3h2l6 3v-9l-6 3zM4.5 9.5l.8 3.5h1.6l-.6-3M12.5 6.3a2 2 0 0 1 0 3.4" />,
  play: <path d="M5 3.2v9.6L12.5 8z" />,
  download: <path d="M8 2.5v8m0 0L4.8 7.3M8 10.5l3.2-3.2M3 13.5h10" />,
  // Catch up (27 Oct 2026): its stack of cards, and taking the last step back.
  cards: <path d="M4.5 4.5h9v9h-9zM2.5 11.5v-9h9" />,
  undo: <path d="M5.5 3L2.5 6l3 3M2.5 6h7a3.5 3.5 0 0 1 0 7H7" />,
  // A pull request (2 Nov 2026): two commits joined, and the branch coming back.
  pr: (
    <path d="M3 4a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0-3 0M3 12a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0-3 0M10 12a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0-3 0M4.5 5.5v5M11.5 10.5V6.5a2 2 0 0 0-2-2H7.5M9 3L7.5 4.5 9 6" />
  ),
  // Where a task came from (30 Oct 2026): a form's page.
  form: <path d="M4 2h8v12H4zM6 5h4M6 7.8h4M6 10.6h2.5" />,
};
export function Icon({ n, size = 16, className }: { n: keyof typeof P | string; size?: number; className?: string }) {
  return (
    <svg
      className={'tfi' + (className ? ` ${className}` : '')}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {P[n]}
    </svg>
  );
}

/** The ring before a status name: dotted for the starting status, a check for closed ones, a ring otherwise. */
export function StatusRing({ s, size = 12 }: { s: TfStatus; size?: number }) {
  if (s.isClosed)
    return (
      <svg width={size} height={size} viewBox="0 0 12 12" aria-hidden="true" className="tfring">
        <circle cx="6" cy="6" r="5.5" fill="currentColor" />
        <path
          d="M3.4 6.2l1.7 1.7 3.4-3.6"
          stroke="var(--tf-ring-check, #fff)"
          strokeWidth="1.4"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" aria-hidden="true" className="tfring">
      <circle
        cx="6"
        cy="6"
        r="4.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeDasharray={s.isInitial ? '1.6 1.4' : undefined}
      />
      {!s.isInitial && <path d="M6 2.6A3.4 3.4 0 0 1 6 9.4z" fill="currentColor" />}
    </svg>
  );
}

/** Light statuses (the starting one) are grey chips with dark text; others are solid with white text, as in ClickUp. */
export function StatusPill({ s, dot }: { s: TfStatus; dot?: boolean }) {
  if (dot) return <i className="tfdot" style={{ background: s.color }} title={s.name} />;
  const light = s.isInitial;
  return (
    <span className={'tfpill' + (light ? ' light' : '')} style={light ? undefined : { background: s.color }}>
      <StatusRing s={s} />
      {s.name}
    </span>
  );
}

export function TypeIcon({ t, size = 14 }: { t: TfType; size?: number }) {
  return (
    <span className="tftype" style={{ color: t.color, fontSize: size }} title={t.name}>
      <TypeGlyph icon={t.icon} size={size} />
    </span>
  );
}

export function PriorityFlag({ p, bare }: { p: { label: string; color: string } | undefined; bare?: boolean }) {
  if (!p)
    return (
      <span className="tfprio none" title="No priority">
        <Icon n="flag" />
      </span>
    );
  return (
    <span className="tfprio">
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M3.5 14V2.5" stroke={p.color} strokeWidth="1.4" strokeLinecap="round" />
        <path d="M3.5 3h8l-1.6 2.8L11.5 9h-8z" fill={p.color} />
      </svg>
      {!bare && p.label}
    </span>
  );
}

// Deep enough that white initials read at 4.5:1 or better (17 Oct 2026 audit: the old teal and orange did not).
const PALETTE = ['#5a43d6', '#0b62c4', '#00796b', '#b0174f', '#8a2a9c', '#b8470a', '#1f7a4d', '#7d5f52'];
export function colorOf(name: string): string {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}
export function Avatar({ name, size = 24, title }: { name: string; size?: number; title?: string }) {
  return (
    <i
      className="tfav"
      style={{ background: colorOf(name), width: size, height: size, fontSize: size * 0.42 }}
      title={title ?? name}
    >
      {initials(name)}
    </i>
  );
}

/** A person on hover: name, team and line manager (20 Oct 2026); an outside colleague's company (29 Oct 2026). */
export const personTitle = (p: Pick<TfPerson, 'name' | 'team' | 'lead' | 'outside'>) =>
  [p.name, p.outside ? `Outside colleague, ${p.team}` : p.team, p.lead ? `Line manager: ${p.lead}` : '']
    .filter(Boolean)
    .join(' · ');

const RECENT = 'tf-recent-people';
const recentPeople = (): string[] => {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
};
/** People just picked go to the front of this browser's recent list (kept to 12). */
const usedPeople = (ids: string[]) => {
  if (!ids.length) return;
  try {
    localStorage.setItem(
      RECENT,
      JSON.stringify([...ids, ...recentPeople().filter((x) => !ids.includes(x))].slice(0, 12)),
    );
  } catch {
    // Not kept; the order falls back to alphabetical.
  }
};

/** Everyone on a task (any role), once each. */
export function Avatars({
  meta,
  roles,
  max = 4,
}: {
  meta: TfMeta;
  roles: Array<{ employeeId: string }>;
  max?: number;
}) {
  const ids = [...new Set(roles.map((r) => r.employeeId))];
  if (!ids.length)
    return (
      <span className="tfnobody" title="Nobody yet">
        <Icon n="user" />
      </span>
    );
  const people = ids.map((id) => meta.people.find((p) => p.id === id)).filter((p): p is TfPerson => !!p);
  return (
    <span className="tfavatars" title={people.map(personTitle).join('\n')}>
      {people.slice(0, max).map((p) => (
        <Avatar key={p.id} name={p.name} title={personTitle(p)} />
      ))}
      {people.length > max && <i className="tfav more">+{people.length - max}</i>}
    </span>
  );
}

/** Pick people (employees) with a search box; ticks show at once and are saved in the background. */
export function PeoplePicker({
  people,
  value,
  onChange,
  label,
  empty = 'Empty',
  toolbar,
  startOpen,
  onClose,
  single,
}: {
  people: TfPerson[];
  value: string[];
  onChange: (ids: string[]) => void;
  label: string;
  /** One person only (a people field set to one person): picking someone replaces whoever was there. */
  single?: boolean;
  empty?: string;
  /** Toolbar style (a filter button), rather than a field value. */
  toolbar?: boolean;
  /** Open straight away (a list cell being edited), and say when it closes. */
  startOpen?: boolean;
  onClose?: () => void;
}) {
  const [open, setOpenState] = useState(!!startOpen);
  const setOpen = (v: boolean) => {
    setOpenState(v);
    if (!v) onClose?.();
  };
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(value);
  const key = value.join(',');
  useEffect(() => setSel(key ? key.split(',') : []), [key]);
  const pick = (ids: string[]) => {
    usedPeople(ids.filter((id) => !sel.includes(id)));
    setSel(ids);
    onChange(ids);
  };
  const meId = useTfMeta().data?.me.employeeId ?? null;
  const box = useAway(open, () => setOpen(false));
  const words = q.trim().toLowerCase();
  const picked = people.filter((p) => sel.includes(p.id));
  // Me first, then who is picked here, then the people this browser picked lately, then everyone A to Z (20 Oct 2026).
  const rank = (() => {
    const recent = open ? recentPeople() : [];
    return (p: TfPerson) =>
      p.id === meId ? -3 : sel.includes(p.id) ? -2 : recent.includes(p.id) ? -1 + recent.indexOf(p.id) / 100 : 0;
  })();
  const shown = people
    .filter((p) => `${p.name} ${p.team} ${p.empNo}`.toLowerCase().includes(words))
    .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name))
    .slice(0, 60);
  return (
    <div className={'tfpick' + (toolbar ? ' intools' : '')} ref={box}>
      <button
        type="button"
        className={'tfpick-btn' + (sel.length ? ' on' : '')}
        onClick={() => setOpen(!open)}
        aria-label={label}
        aria-expanded={open}
      >
        {toolbar && <Icon n="users" />}
        {picked.length ? (
          toolbar ? (
            <span>{picked.length === 1 ? picked[0]!.name : `${picked.length} people`}</span>
          ) : // One person: avatar and name; several: their avatars only, names on hover (20 Oct 2026).
          picked.length === 1 ? (
            <span className="tfperson" title={personTitle(picked[0]!)}>
              <Avatar name={picked[0]!.name} size={20} title={personTitle(picked[0]!)} />
              {picked[0]!.name}
            </span>
          ) : (
            <span className="tfpeople-avs" title={picked.map(personTitle).join('\n')}>
              {picked.map((p) => (
                <span key={p.id} className="tfpeople-av" title={personTitle(p)}>
                  <Avatar name={p.name} size={24} title={personTitle(p)} />
                </span>
              ))}
            </span>
          )
        ) : (
          <span className="tfempty-v">{toolbar ? label : empty}</span>
        )}
      </button>
      {open && (
        <div className="tfpop" role="dialog" aria-label={label}>
          <div className="tfpop-search">
            <Icon n="search" />
            <input
              autoFocus
              type="search"
              placeholder="Search or enter name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search people"
            />
          </div>
          <div className="tfpick-list">
            {shown.map((p) => (
              <label key={p.id} className={sel.includes(p.id) ? 'on' : ''}>
                <input
                  type={single ? 'radio' : 'checkbox'}
                  checked={sel.includes(p.id)}
                  onChange={() => {
                    if (!single) return pick(sel.includes(p.id) ? sel.filter((x) => x !== p.id) : [...sel, p.id]);
                    pick([p.id]);
                    setOpen(false);
                  }}
                  onClick={() => single && sel.length === 1 && sel[0] === p.id && (pick([]), setOpen(false))}
                />
                <Avatar name={p.name} size={26} title={personTitle(p)} />
                <span>
                  {p.name}
                  {p.outside && <span className="tfpick-co"> ({p.team})</span>}
                  {p.id === meId && <em className="tfpick-me">Me</em>}
                  <small>
                    {p.outside ? 'Outside colleague' : p.team}
                    {p.lead ? ` · ${p.lead}` : ''}
                  </small>
                </span>
              </label>
            ))}
            {!shown.length && <p className="tfmuted tfpad8">Nobody matches.</p>}
          </div>
          {sel.length > 0 && (
            <button type="button" className="tfbtn ghost sm" onClick={() => pick([])}>
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ a one-choice menu (in place of a native select)

/**
 * A button that opens a menu of choices, ticking the current one (17 Oct 2026): our own menu, not the browser's select,
 * so it looks like the rest of Task Force and opens as a sheet on a phone.
 */
export function SelectMenu<T extends string | number | null>({
  value,
  options,
  onChange,
  label,
  className,
  disabled,
  title,
  children,
}: {
  value: T;
  options: Array<{ v: T; label: string; icon?: ReactNode }>;
  onChange: (v: T) => void;
  label: string;
  className?: string;
  disabled?: boolean;
  /** A tooltip (why it is disabled …). */
  title?: string;
  /** What the button shows. */
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const box = useAway<HTMLSpanElement>(open, () => setOpen(false));
  return (
    <span className={'tfselmenu' + (className ? ` ${className}` : '')} ref={box}>
      <button
        type="button"
        className="tfselmenu-btn"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        disabled={disabled}
        title={title}
      >
        {children}
      </button>
      {open && (
        <div className="tfpop tfselmenu-pop" role="menu" aria-label={label}>
          <div className="tfpop-h">{label}</div>
          {options.map((o) => (
            <button
              key={String(o.v)}
              type="button"
              role="menuitemradio"
              aria-checked={o.v === value}
              className="tfmenu-item"
              onClick={() => {
                setOpen(false);
                if (o.v !== value) onChange(o.v);
              }}
            >
              {o.icon}
              <span className="tfgrow">{o.label}</span>
              {o.v === value && <Icon n="check" size={14} />}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
